import React, { useState } from 'react';
import { addDays } from 'date-fns';

export default function BulkSale({ saleContext }) {
    const [learners, setLearners] = useState([{
        id: 1,
        name: '',
        contactNumber: '',
        contactEmail: '',
        enrollmentEmail: '',
        enrollmentEmailSameAsContact: false,
        program: '',
        validity: '7 days',
        startDate: '',
        endDate: '',
        accessDays: 7,
    }]);

    const [paymentDetails, setPaymentDetails] = useState({
        currency: 'INR',
        totalMrp: '',
        bulkSalePrice: '',
        bulkDiscount: '',
        offer: '',
        paymentDate: '',
        paymentGateway: '',
        transactionId: '',
        saleOwner: '',
        teamLead: ''
    });

    const handleAddLearner = () => {
        setLearners([...learners, {
            id: learners.length + 1,
            name: '',
            contactNumber: '',
            contactEmail: '',
            enrollmentEmail: '',
            enrollmentEmailSameAsContact: false,
            program: '',
            validity: '7 days',
            startDate: '',
            endDate: '',
            accessDays: 7,
        }]);
    };

    const updateLearner = (index, field, value) => {
        const updatedLearners = [...learners];
        updatedLearners[index][field] = value;

        // Handle enrollment email checkbox
        if (field === 'enrollmentEmailSameAsContact') {
            updatedLearners[index].enrollmentEmail = value ? 
                updatedLearners[index].contactEmail : '';
        }

        // Handle contact email change when enrollment email is same
        if (field === 'contactEmail' && updatedLearners[index].enrollmentEmailSameAsContact) {
            updatedLearners[index].enrollmentEmail = value;
        }

        // Handle validity and dates
        if (field === 'startDate' || field === 'validity') {
            const days = field === 'validity' ? 
                (value === '7 days' ? 7 : value === '1 Month' ? 30 : 180) : 
                updatedLearners[index].accessDays;

            updatedLearners[index].accessDays = days;

            if (updatedLearners[index].startDate) {
                const endDate = addDays(new Date(updatedLearners[index].startDate), days);
                updatedLearners[index].endDate = endDate.toISOString().split('T')[0];
            }
        }

        setLearners(updatedLearners);
    };

    const handlePaymentChange = (field, value) => {
        const updates = { [field]: value };
        
        // Calculate discount when MRP or sale price changes
        if (field === 'totalMrp' || field === 'bulkSalePrice') {
            const mrp = field === 'totalMrp' ? value : paymentDetails.totalMrp;
            const salePrice = field === 'bulkSalePrice' ? value : paymentDetails.bulkSalePrice;
            
            if (mrp && salePrice) {
                updates.bulkDiscount = parseInt(mrp) - parseInt(salePrice);
            }
        }

        setPaymentDetails(prev => ({
            ...prev,
            ...updates
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Handle bulk sale submission
    };

    return (
        <form onSubmit={handleSubmit}>
            {learners.map((learner, index) => (
                <div key={learner.id} className='card p-4 border-0 shadow background-dark-secondary mb-4'>
                    <div className="card-body">
                        <h2 className='text-white fs-20 mb-3'>Learner {index + 1}</h2>
                        <div className='row'>
                            <div className="col-6">
                                <div className="form-group mb-3">
                                    <label className="text-white mb-2">Name</label>
                                    <input 
                                        type="text" 
                                        className='form-control background-dark-tertiary'
                                        value={learner.name}
                                        onChange={(e) => updateLearner(index, 'name', e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="form-group mb-3">
                                    <label className="text-white mb-2">Contact Number</label>
                                    <input 
                                        type="tel" 
                                        className='form-control background-dark-tertiary'
                                        value={learner.contactNumber}
                                        onChange={(e) => updateLearner(index, 'contactNumber', e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="form-group mb-3">
                                    <label className="text-white mb-2">Contact Email</label>
                                    <input 
                                        type="email" 
                                        className='form-control background-dark-tertiary'
                                        value={learner.contactEmail}
                                        onChange={(e) => updateLearner(index, 'contactEmail', e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="form-group mb-2">
                                    <label className="text-white mb-2">Enrollment Email</label>
                                    <input 
                                        type="email" 
                                        className='form-control background-dark-tertiary'
                                        value={learner.enrollmentEmail}
                                        onChange={(e) => updateLearner(index, 'enrollmentEmail', e.target.value)}
                                        disabled={learner.enrollmentEmailSameAsContact}
                                        required
                                    />
                                </div>
                                <div className="form-check mb-3">
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox"
                                        id={`enrollmentCheckbox-${learner.id}`}
                                        checked={learner.enrollmentEmailSameAsContact}
                                        onChange={(e) => updateLearner(index, 'enrollmentEmailSameAsContact', e.target.checked)}
                                    />
                                    <label 
                                        className="form-check-label text-white"
                                        htmlFor={`enrollmentCheckbox-${learner.id}`}
                                    >
                                        Same as Contact Email
                                    </label>
                                </div>
                            </div>
                            <div className="col-12">
                                <div className="form-group mb-3">
                                    <label className="text-white mb-2">Program</label>
                                    <select 
                                        className="form-select background-dark-tertiary"
                                        value={learner.program}
                                        onChange={(e) => updateLearner(index, 'program', e.target.value)}
                                        required
                                    >
                                        <option value="" disabled>Select Program</option>
                                        {saleContext?.programs?.map((program) => (
                                            <option key={program.id} value={program.id}>
                                                {program.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="form-group mb-3">
                                    <label className="text-white mb-2">Validity</label>
                                    <select 
                                        className="form-select background-dark-tertiary"
                                        value={learner.validity}
                                        onChange={(e) => updateLearner(index, 'validity', e.target.value)}
                                        required
                                    >
                                        <option value="7 days">7 days</option>
                                        <option value="1 Month">1 Month</option>
                                        <option value="6 Months">6 Months</option>
                                    </select>
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="form-group mb-3">
                                    <label className="text-white mb-2">Start Date</label>
                                    <input 
                                        type="date"
                                        className='form-control background-dark-tertiary'
                                        value={learner.startDate}
                                        onChange={(e) => updateLearner(index, 'startDate', e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="form-group mb-3">
                                    <label className="text-white mb-2">Expiry Date</label>
                                    <input 
                                        type="date"
                                        className='form-control background-dark-tertiary'
                                        value={learner.endDate}
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
            
            <button 
                type="button"
                className='btn btn-dark btn-dark-primary px-4 py-2 mb-3'
                onClick={handleAddLearner}
            >
                Add Another Learner
            </button>

            <div className='card p-4 border-0 shadow background-dark-secondary mb-4'>
                <div className="card-body">
                    <h2 className='text-white fs-20 mb-3'>Payment Details</h2>
                    <div className='row'>
                        <div className="col-6">
                            <div className="form-group mb-3">
                                <label className="text-white mb-2">Currency</label>
                                <select 
                                    className="form-select background-dark-tertiary"
                                    value={paymentDetails.currency}
                                    onChange={(e) => handlePaymentChange('currency', e.target.value)}
                                    required
                                >
                                    <option value="INR">INR</option>
                                    <option value="USD">USD</option>
                                </select>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="form-group mb-3">
                                <label className="text-white mb-2">Total MRP</label>
                                <input 
                                    type="number" 
                                    className='form-control background-dark-tertiary'
                                    value={paymentDetails.totalMrp}
                                    onChange={(e) => handlePaymentChange('totalMrp', e.target.value)}
                                    required
                                />
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="form-group mb-3">
                                <label className="text-white mb-2">Bulk Sale Price</label>
                                <input 
                                    type="number" 
                                    className='form-control background-dark-tertiary'
                                    value={paymentDetails.bulkSalePrice}
                                    onChange={(e) => handlePaymentChange('bulkSalePrice', e.target.value)}
                                    required
                                />
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="form-group mb-3">
                                <label className="text-white mb-2">Bulk Discount</label>
                                <input 
                                    type="number" 
                                    className='form-control background-dark-tertiary'
                                    value={paymentDetails.bulkDiscount}
                                    readOnly
                                />
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="form-group mb-3">
                                <label className="text-white mb-2">Offer</label>
                                <select 
                                    className="form-select background-dark-tertiary"
                                    value={paymentDetails.offer}
                                    onChange={(e) => handlePaymentChange('offer', e.target.value)}
                                >
                                    <option value="" disabled>Select Offer</option>
                                    {saleContext?.offer_type?.map((offer) => (
                                        <option key={offer.id} value={offer.id}>{offer.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="form-group mb-3">
                                <label className="text-white mb-2">Payment Date</label>
                                <input 
                                    type="date"
                                    className='form-control background-dark-tertiary'
                                    value={paymentDetails.paymentDate}
                                    onChange={(e) => handlePaymentChange('paymentDate', e.target.value)}
                                    required
                                />
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="form-group mb-3">
                                <label className="text-white mb-2">Payment Gateway</label>
                                <select 
                                    className="form-select background-dark-tertiary"
                                    value={paymentDetails.paymentGateway}
                                    onChange={(e) => handlePaymentChange('paymentGateway', e.target.value)}
                                    required
                                >
                                    <option value="" disabled>Select Payment Gateway</option>
                                    {saleContext?.payment_gateways?.map((gateway) => (
                                        <option key={gateway.id} value={gateway.id}>{gateway.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="form-group mb-3">
                                <label className="text-white mb-2">Transaction ID</label>
                                <input 
                                    type="text"
                                    className='form-control background-dark-tertiary'
                                    value={paymentDetails.transactionId}
                                    onChange={(e) => handlePaymentChange('transactionId', e.target.value)}
                                    required
                                />
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="form-group mb-3">
                                <label className="text-white mb-2">Sale Owner</label>
                                <input 
                                    type="email"
                                    className='form-control background-dark-tertiary'
                                    value={paymentDetails.saleOwner}
                                    onChange={(e) => handlePaymentChange('saleOwner', e.target.value)}
                                    required
                                />
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="form-group mb-3">
                                <label className="text-white mb-2">TL</label>
                                <input 
                                    type="email"
                                    className='form-control background-dark-tertiary'
                                    value={paymentDetails.teamLead}
                                    onChange={(e) => handlePaymentChange('teamLead', e.target.value)}
                                    required
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <button 
                type="submit" 
                className='btn btn-dark btn-dark-primary px-5 py-2 mt-3 fw-bold ms-auto d-table'
            >
                Submit Bulk Sale
            </button>
        </form>
    );
}
